import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import posthog from "posthog-js";
import { trpc } from "@/lib/trpc";

interface SignupSurveyModalProps {
  open: boolean;
  userId?: string;
  onClose: () => void;
}

type Step = {
  key: "role" | "referral";
  title: string;
  options: string[];
  multi?: boolean;
};

const STEPS: Step[] = [
  {
    key: "role",
    title: "What best describes you?",
    options: [
      "Musician / Singer / Producer",
      "Content Creator / Influencer",
      "Business / Marketing",
      "Just for fun / Experimenting",
      "Student",
      "Other",
    ],
  },
  {
    key: "referral",
    title: "How did you hear about us?",
    options: [
      "Google / Bing",
      "ChatGPT / Grok / Gemini / Claude",
      "Friends / Colleagues",
      "Facebook",
      "TikTok",
      "Instagram",
      "YouTube",
      "Reddit",
      "Other",
    ],
  },
];

export default function SignupSurveyModal({
  open,
  userId,
  onClose,
}: SignupSurveyModalProps) {
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const shownTrackedRef = useRef(false);
  const markShownMutation = trpc.auth.markSignupSurveyShown.useMutation();

  useEffect(() => {
    if (open && !shownTrackedRef.current) {
      shownTrackedRef.current = true;
      try {
        posthog.capture("signup_survey_shown");
      } catch {}
      // Stamp the server the moment the survey is shown so it can never
      // reappear on a later login, another device, or after cleared storage.
      // Deliberately on SHOW, not completion: closing the tab mid-survey
      // must not re-trigger it next session.
      try {
        markShownMutation.mutate();
      } catch {}
    }
  }, [open]);

  const step = STEPS[stepIdx];
  const current = answers[step.key];
  const selectedSet = new Set(
    Array.isArray(current) ? current : current ? [current] : [],
  );

  const markCompleted = () => {
    try {
      localStorage.setItem("signupSurveyCompleted", "1");
      if (userId) {
        localStorage.setItem(`signupSurveyCompleted_${userId}`, "1");
      }
    } catch {}
  };

  const finish = (final: Record<string, string | string[]>) => {
    try {
      // $set mirrors the answers onto the person profile so cohorts and
      // funnel breakdowns can segment by them, not just by this one event.
      posthog.capture("signup_survey_completed", {
        ...final,
        $set: {
          survey_role: final.role,
          survey_referral: final.referral,
        },
      });
    } catch {}
    markCompleted();
    onClose();
  };

  // ESC / overlay click / X — treat as a permanent skip so the survey never
  // nags the same person twice.
  const dismiss = () => {
    try {
      posthog.capture("signup_survey_dismissed", {
        step: step.key,
        step_index: stepIdx,
        ...answers,
      });
    } catch {}
    markCompleted();
    onClose();
  };

  const advance = (next: Record<string, string | string[]>) => {
    if (stepIdx < STEPS.length - 1) {
      setStepIdx(stepIdx + 1);
    } else {
      finish(next);
    }
  };

  const handleSelect = (option: string) => {
    if (step.multi) {
      const prev = Array.isArray(current) ? current : [];
      const nextValues = prev.includes(option)
        ? prev.filter((v) => v !== option)
        : [...prev, option];
      setAnswers({ ...answers, [step.key]: nextValues });
    } else {
      const next = { ...answers, [step.key]: option };
      setAnswers(next);
      advance(next);
    }
  };

  const handleContinue = () => {
    advance(answers);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && dismiss()}>
      <DialogContent className="max-w-2xl border-white/10 bg-neutral-900 text-white p-8 sm:p-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="inline-flex items-center justify-center rounded-full bg-indigo-500/30 text-indigo-200 text-xs font-medium px-2.5 py-1">
            {stepIdx + 1}/{STEPS.length}
          </span>
          <h2 className="text-xl sm:text-2xl font-semibold">{step.title}</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {step.options.map((option) => {
            const selected = selectedSet.has(option);
            return (
              <button
                key={option}
                type="button"
                onClick={() => handleSelect(option)}
                className={`relative rounded-xl border px-4 py-4 text-sm text-left sm:text-center transition-colors ${
                  selected
                    ? "border-indigo-400 bg-indigo-500/10"
                    : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"
                }`}
              >
                <span className="block">{option}</span>
                {selected && (
                  <span className="absolute right-2 bottom-2 inline-flex items-center justify-center h-5 w-5 rounded-full bg-indigo-500 text-white">
                    <Check className="h-3 w-3" />
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between mt-8">
          <button
            type="button"
            onClick={() => stepIdx > 0 && setStepIdx(stepIdx - 1)}
            className={`text-sm text-neutral-400 hover:text-neutral-200 transition-colors ${
              stepIdx === 0 ? "invisible" : ""
            }`}
          >
            Back
          </button>
          {step.multi && (
            <Button
              onClick={handleContinue}
              disabled={selectedSet.size === 0}
              className="bg-indigo-500 hover:bg-indigo-400 text-white"
            >
              {stepIdx === STEPS.length - 1 ? "Finish" : "Continue"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
